import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useLanguage } from '../contexts/LanguageContext';
import {
  AreaChart, Area, BarChart, Bar, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend
} from 'recharts';
import {
  TrendUp, Package, ShoppingCart, CurrencyDollar,
  Warning, ArrowClockwise, CircleNotch
} from '@phosphor-icons/react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CHART_COLORS = ['var(--primary)', '#3B82F6', '#F97316', '#8B5CF6', '#EF4444', '#14B8A6', '#EAB308', '#EC4899'];

function StatCard({ icon: Icon, label, value, sub, color }) {
  return (
    <div className="bg-[var(--card)] border border-[var(--dm-border)] rounded-xl p-5 flex items-start gap-4" data-testid={`stat-${label.toLowerCase().replace(/\s/g, '-')}`}>
      <div className="w-11 h-11 rounded-lg flex items-center justify-center flex-shrink-0" style={{ background: `${color}20` }}>
        <Icon size={22} style={{ color }} />
      </div>
      <div>
        <p className="text-[var(--dm-text-muted)] text-sm">{label}</p>
        <p className="text-[var(--text)] text-2xl font-bold font-mono mt-1">{value}</p>
        {sub && <p className="text-xs text-[var(--dm-text-muted)] mt-1">{sub}</p>}
      </div>
    </div>
  );
}

export default function Dashboard() {
  const { t } = useLanguage();
  const [stats, setStats] = useState(null);
  const [trend, setTrend] = useState([]);
  const [topSelling, setTopSelling] = useState([]);
  const [categorySales, setCategorySales] = useState([]);
  const [lowStock, setLowStock] = useState([]);
  const [loading, setLoading] = useState(true);
  const [trendDays, setTrendDays] = useState(7);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [statsRes, trendRes, topRes, catRes, lowRes] = await Promise.all([
        axios.get(`${API_URL}/api/dashboard/stats`, { withCredentials: true }),
        axios.get(`${API_URL}/api/dashboard/sales-trend?days=${trendDays}`, { withCredentials: true }),
        axios.get(`${API_URL}/api/dashboard/top-selling?limit=5`, { withCredentials: true }),
        axios.get(`${API_URL}/api/dashboard/category-sales`, { withCredentials: true }),
        axios.get(`${API_URL}/api/dashboard/low-stock?limit=5`, { withCredentials: true }),
      ]);
      setStats(statsRes.data);
      setTrend(trendRes.data);
      setTopSelling(topRes.data);
      setCategorySales(catRes.data);
      setLowStock(lowRes.data);
    } catch (err) {
      console.error('Dashboard fetch error:', err);
    }
    setLoading(false);
  }, [trendDays]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const fmt = (n) => `$${(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  const fmtShort = (n) => {
    const d = new Date(n + 'T00:00:00');
    return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-32">
        <CircleNotch size={40} className="animate-spin text-[var(--primary)]" />
      </div>
    );
  }

  const tooltipStyle = { backgroundColor: 'var(--card)', border: '1px solid var(--dm-border)', borderRadius: '8px', color: 'var(--text)', fontSize: '13px' };

  return (
    <div className="space-y-6" data-testid="dashboard-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-[var(--text)]">{t('dashboard') || 'Dashboard'}</h2>
          <p className="text-sm text-[var(--dm-text-muted)]">{t('dashboardSubtitle') || 'Business overview at a glance'}</p>
        </div>
        <button onClick={fetchData} className="px-4 py-2 bg-[var(--card)] border border-[var(--dm-border)] text-[var(--dm-text-muted)] rounded-lg hover:bg-[var(--dm-bg-card-hover)] transition-colors flex items-center gap-2 text-sm" data-testid="refresh-dashboard">
          <ArrowClockwise size={16} /> Refresh
        </button>
      </div>

      {/* Stat Cards */}
      {stats && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <StatCard icon={CurrencyDollar} label="Today's Sales" value={fmt(stats.today_sales)} sub={`${stats.today_invoices} invoices`} color="var(--primary)" />
          <StatCard icon={ShoppingCart} label="Total Products" value={stats.total_products} sub={`Stock value: ${fmt(stats.stock_value)}`} color="#3B82F6" />
          <StatCard icon={Warning} label="Low Stock" value={stats.low_stock_items} sub={`${stats.out_of_stock_items} out of stock`} color="#F97316" />
          <StatCard icon={TrendUp} label="Returns Today" value={stats.today_returns} sub={`${stats.current_stock} units total`} color="#8B5CF6" />
        </div>
      )}

      {/* Row: Sales Trend + Top Selling */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Sales Trend Area Chart */}
        <div className="lg:col-span-2 bg-[var(--card)] border border-[var(--dm-border)] rounded-xl p-5" data-testid="sales-trend-chart">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-[var(--text)] font-semibold">Sales Trend</h3>
            <div className="flex gap-1">
              {[7, 14, 30].map(d => (
                <button key={d} onClick={() => setTrendDays(d)}
                  className={`px-3 py-1 text-xs rounded-lg font-medium transition-colors ${trendDays === d ? 'bg-[var(--primary)] text-white' : 'bg-[var(--dm-bg-card-hover)] text-[var(--dm-text-muted)]'}`}
                  data-testid={`trend-${d}d`}>{d}D</button>
              ))}
            </div>
          </div>
          <ResponsiveContainer width="100%" height={260}>
            <AreaChart data={trend}>
              <defs>
                <linearGradient id="colorRevenue" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="var(--primary)" stopOpacity={0.3}/>
                  <stop offset="95%" stopColor="var(--primary)" stopOpacity={0}/>
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="var(--dm-border)" opacity={0.5} />
              <XAxis dataKey="date" tickFormatter={fmtShort} tick={{ fill: 'var(--dm-text-muted)', fontSize: 12 }} axisLine={false} tickLine={false} />
              <YAxis tick={{ fill: 'var(--dm-text-muted)', fontSize: 12 }} axisLine={false} tickLine={false} tickFormatter={(v) => `$${v}`} />
              <Tooltip contentStyle={tooltipStyle} formatter={(v) => [`$${v.toFixed(2)}`, 'Revenue']} labelFormatter={fmtShort} />
              <Area type="monotone" dataKey="revenue" stroke="var(--primary)" strokeWidth={2} fillOpacity={1} fill="url(#colorRevenue)" />
            </AreaChart>
          </ResponsiveContainer>
        </div>

        {/* Top Selling Bar Chart */}
        <div className="bg-[var(--card)] border border-[var(--dm-border)] rounded-xl p-5" data-testid="top-selling-chart">
          <h3 className="text-[var(--text)] font-semibold mb-4">Top Selling Products</h3>
          {topSelling.length > 0 ? (
            <ResponsiveContainer width="100%" height={260}>
              <BarChart data={topSelling} layout="vertical" margin={{ left: 10 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="var(--dm-border)" opacity={0.5} horizontal={false} />
                <XAxis type="number" tick={{ fill: 'var(--dm-text-muted)', fontSize: 11 }} axisLine={false} tickLine={false} />
                <YAxis type="category" dataKey="product_name" tick={{ fill: 'var(--dm-text-muted)', fontSize: 11 }} axisLine={false} tickLine={false} width={100} />
                <Tooltip contentStyle={tooltipStyle} formatter={(v) => [v, 'Qty Sold']} />
                <Bar dataKey="total_quantity" fill="var(--primary)" radius={[0, 6, 6, 0]} barSize={20} />
              </BarChart>
            </ResponsiveContainer>
          ) : <p className="text-[var(--dm-text-muted)] text-sm text-center py-16">No sales data yet</p>}
        </div>
      </div>

      {/* Row: Category Pie + Low Stock */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Category Sales Pie Chart */}
        <div className="bg-[var(--card)] border border-[var(--dm-border)] rounded-xl p-5" data-testid="category-chart">
          <h3 className="text-[var(--text)] font-semibold mb-4">Sales by Category</h3>
          {categorySales.length > 0 ? (
            <ResponsiveContainer width="100%" height={260}>
              <PieChart>
                <Pie data={categorySales} dataKey="revenue" nameKey="category" cx="50%" cy="50%" outerRadius={90} innerRadius={50} paddingAngle={2} strokeWidth={0}>
                  {categorySales.map((_, i) => <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />)}
                </Pie>
                <Tooltip contentStyle={tooltipStyle} formatter={(v) => [`$${v.toFixed(2)}`, 'Revenue']} />
                <Legend wrapperStyle={{ fontSize: '12px', color: 'var(--dm-text-muted)' }} />
              </PieChart>
            </ResponsiveContainer>
          ) : <p className="text-[var(--dm-text-muted)] text-sm text-center py-16">No category data</p>}
        </div>

        {/* Low Stock Alert List */}
        <div className="lg:col-span-2 bg-[var(--card)] border border-[var(--dm-border)] rounded-xl p-5" data-testid="low-stock-list">
          <div className="flex items-center gap-2 mb-4">
            <Warning size={20} className="text-[#F97316]" />
            <h3 className="text-[var(--text)] font-semibold">Low Stock Alerts</h3>
          </div>
          {lowStock.length > 0 ? (
            <div className="space-y-3">
              {lowStock.map((p, i) => (
                <div key={i} className="flex items-center justify-between py-3 border-b border-[var(--dm-border)] last:border-0">
                  <div>
                    <p className="text-[var(--text)] font-medium text-sm">{p.name}</p>
                    <p className="text-xs text-[var(--dm-text-muted)]">{p.category || 'No category'} &middot; Reorder at {p.reorder_level}</p>
                  </div>
                  <div className="text-right">
                    <span className={`text-lg font-bold font-mono ${p.current_stock === 0 ? 'text-[#EF4444]' : 'text-[#F97316]'}`}>{p.current_stock}</span>
                    <p className="text-xs text-[var(--dm-text-muted)]">{p.current_stock === 0 ? 'Out of stock' : 'Low stock'}</p>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-12">
              <Package size={40} className="text-[var(--primary)] mx-auto mb-2 opacity-50" />
              <p className="text-[var(--dm-text-muted)] text-sm">All products well stocked!</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
